import partial from 'lodash.partial';
import { rechargeApiRequest } from '../utils/request';
import {
  CancelSubscriptionRequest,
  CreateSubscriptionRequest,
  Subscription,
  SubscriptionsResponse,
  SubscriptionListParams,
  UpdateSubscriptionRequest,
  UpdateSubscriptionParams,
  GetSubscriptionOptions,
  BasicSubscriptionParams,
  Subscription_2021_01,
  UpdateSubscriptionsRequest,
  UpdateSubscriptionsParams,
} from '../types/subscription';
import { IsoDateString } from '../types/common';
import { Session } from '../types/session';
import { ChargeResponse, CreateRecipientAddress, Onetime } from '../types';
import {
  bulkSubscriptionCreateMapper,
  bulkSubscriptionUpdateMapper,
  subscriptionMapperOldToNew,
} from '../mappers/subscription';

export async function getSubscription(
  session: Session,
  id: string | number,
  options?: GetSubscriptionOptions
): Promise<Subscription> {
  const { subscription } = await rechargeApiRequest<{ subscription: Subscription }>(
    'get',
    `/subscriptions`,
    {
      id,
      query: { include: options?.include },
    },
    session
  );
  return subscription;
}

export function listSubscriptions(session: Session, query?: SubscriptionListParams): Promise<SubscriptionsResponse> {
  return rechargeApiRequest<SubscriptionsResponse>('get', `/subscriptions`, { query }, session);
}

/**
 * When creating a subscription via API, order_interval_frequency and charge_interval_frequency values do not necessarily
 * need to match the values set in the respective Plans. The product, however, does need to have at least one Plan in order
 * to be added to a subscription.
 */
export async function createSubscription(
  session: Session,
  createRequest: CreateSubscriptionRequest,
  query?: BasicSubscriptionParams
): Promise<Subscription> {
  const { subscription } = await rechargeApiRequest<{ subscription: Subscription }>(
    'post',
    `/subscriptions`,
    {
      data: createRequest,
      query,
    },
    session
  );
  return subscription;
}

/**
 * Updating parameters like frequency, charge_interval_frequency, order_interval_frequency, order_interval_unit will cause our algorithm to automatically recalculate the next charge date (next_charge_scheduled_at).
 * WARNING: This update will remove skipped and manually changed charges.
 * If you want to change the next charge date (next_charge_scheduled_at) we recommend you to update these parameters first.
 * When updating order_interval_unit OR order_interval_frequency OR charge_interval_frequency all three parameters are required.
 */
export async function updateSubscription(
  session: Session,
  id: string | number,
  updateRequest: UpdateSubscriptionRequest,
  query?: UpdateSubscriptionParams
): Promise<Subscription> {
  const { subscription } = await rechargeApiRequest<{ subscription: Subscription }>(
    'put',
    `/subscriptions`,
    {
      id,
      data: updateRequest,
      query,
    },
    session
  );
  return subscription;
}

/**
 * If there are two active subscriptions with the same address_id, and you update their
 * next_charge_date parameters to match, their charges will get merged into a new charge
 * with a new id
 */
export async function updateSubscriptionChargeDate(
  session: Session,
  id: string | number,
  date: IsoDateString,
  query?: BasicSubscriptionParams
): Promise<Subscription> {
  const { subscription } = await rechargeApiRequest<{ subscription: Subscription }>(
    'post',
    `/subscriptions/${id}/set_next_charge_date`,
    {
      data: { date },
      query,
    },
    session
  );
  return subscription;
}

export async function updateSubscriptionAddress(
  session: Session,
  id: string | number,
  address_id: string | number
): Promise<Subscription> {
  const { subscription } = await rechargeApiRequest<{ subscription: Subscription }>(
    'post',
    `/subscriptions/${id}/change_address`,
    {
      data: { address_id },
    },
    session
  );
  return subscription;
}

/**
 * An involuntary subscription cancelled due to max retries reached will trigger the
 * charge/max_retries_reached webhook. If this leads to the subscription being cancelled,
 * the subscription/cancelled webhook will trigger.
 */
export async function cancelSubscription(
  session: Session,
  id: string | number,
  cancelRequest: CancelSubscriptionRequest,
  query?: BasicSubscriptionParams
): Promise<Subscription> {
  const { subscription } = await rechargeApiRequest<{ subscription: Subscription }>(
    'post',
    `/subscriptions/${id}/cancel`,
    {
      data: cancelRequest,
      query,
    },
    session
  );
  return subscription;
}

/**
 * When activating subscription, following attributes will be set to null: cancelled_at, cancellation_reason
 * and cancellation_reason_comments.
 */
export async function activateSubscription(
  session: Session,
  id: string | number,
  query?: BasicSubscriptionParams
): Promise<Subscription> {
  const { subscription } = await rechargeApiRequest<{ subscription: Subscription }>(
    'post',
    `/subscriptions/${id}/activate`,
    { query },
    session
  );
  return subscription;
}

/* Skip charge associated with a subscription. */
export async function skipSubscriptionCharge(session: Session, id: number | string, date: IsoDateString) {
  const { charge } = await rechargeApiRequest<ChargeResponse>(
    'post',
    `/subscriptions/${id}/charges/skip`,
    {
      data: {
        date,
        subscription_id: `${id}`,
      },
    },
    session
  );
  return charge;
}

/**
 * Gift a subscription to another person. This creates onetime products for the recipient and skips that subscription for the customer.
 * When the gifted onetime ships the recipient receives and email notification.
 */
export async function skipGiftSubscriptionCharge(
  session: Session,
  subscriptionIds: Array<number | string>,
  recipientAddress: CreateRecipientAddress
) {
  const { onetimes } = await rechargeApiRequest<{ onetimes: Onetime[] }>(
    'post',
    '/purchase_items/skip_gift',
    {
      data: {
        purchase_item_ids: subscriptionIds.map(Number),
        recipient_address: recipientAddress,
      },
    },
    session
  );

  return onetimes;
}

/**
 * Bulk create new subscriptions.
 * Must all be for the same address.
 * There is a limit of 20 subscriptions per request.
 */
export async function createSubscriptions(
  session: Session,
  createRequestBulk: CreateSubscriptionRequest[]
): Promise<Subscription[]> {
  // validate size
  const length = createRequestBulk.length;
  if (length < 1 || length > 21) {
    throw new Error('Number of subscriptions must be between 1 and 20.');
  }
  // validate customerId
  const { customerId } = session;
  if (!customerId) {
    throw new Error('No customerId in session.');
  }
  // validate addressId
  const addressId = createRequestBulk[0].address_id;
  if (!createRequestBulk.every(createRequest => createRequest.address_id === addressId)) {
    throw new Error('All subscriptions must have the same address_id.');
  }
  const bulkSubscriptionMapperPartial = partial(bulkSubscriptionCreateMapper, customerId);
  const requestData = createRequestBulk.map(bulkSubscriptionMapperPartial);
  const { subscriptions } = await rechargeApiRequest<{ subscriptions: Subscription_2021_01[] }>(
    'post',
    `/addresses/${addressId}/subscriptions-bulk`,
    {
      data: { subscriptions: requestData },
      headers: {
        'X-Recharge-Version': '2021-01',
      },
    },
    session
  );
  return subscriptions.map(subscriptionMapperOldToNew);
}

/**
 * Bulk create new subscriptions.
 * Must all be for the same address.
 * There is a limit of 20 subscriptions per request.
 * Same rules apply as a single subscription update
 */
export async function updateSubscriptions(
  session: Session,
  addressId: string | number,
  updateRequestBulk: UpdateSubscriptionsRequest[],
  query?: UpdateSubscriptionsParams
): Promise<Subscription[]> {
  // validate size
  const length = updateRequestBulk.length;
  if (length < 1 || length > 21) {
    throw new Error('Number of subscriptions must be between 1 and 20.');
  }
  // validate customerId
  const { customerId } = session;
  if (!customerId) {
    throw new Error('No customerId in session.');
  }
  const bulkSubscriptionMapperPartial = partial(bulkSubscriptionUpdateMapper, !!query?.force_update);
  const requestData = updateRequestBulk.map(bulkSubscriptionMapperPartial);
  const { subscriptions } = await rechargeApiRequest<{ subscriptions: Subscription_2021_01[] }>(
    'put',
    `/addresses/${addressId}/subscriptions-bulk`,
    {
      data: { subscriptions: requestData },
      headers: {
        'X-Recharge-Version': '2021-01',
      },
    },
    session
  );
  return subscriptions.map(subscriptionMapperOldToNew);
}
